import {
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  Pagination,
  Select,
  SimpleGrid,
  Stack,
  Text,
} from '@mantine/core';
import type { QuestionDifficulty, QuestionStatus, QuestionType } from '@prepzio/shared';
import { useState } from 'react';

import { InfoTip } from '../components/InfoTip';
import { QuestionDetailModal, statusColor, useOpenQuestion } from '../components/QuestionInspector';
import { SkillPicker } from '../components/skill-picker';
import { useDrafts, useResetContent } from '../hooks/use-content';
import { useEnumLabel, useT } from '../i18n';
import { errorMessage } from '../lib/ui';

const TYPES: QuestionType[] = ['free_text', 'mcq', 'coding'];
const DIFFICULTIES: QuestionDifficulty[] = ['easy', 'medium', 'hard'];
const STATUSES: QuestionStatus[] = ['published', 'draft', 'rejected'];

/**
 * Browse the bank (PRE-284).
 *
 * Split off the generate screen because the two are different jobs. Generating
 * is deciding once and waiting; browsing is looking for something and narrowing
 * until it appears. Sharing a page meant the filters — status, skill, type,
 * difficulty — sat beside a form whose fields had the same four names and meant
 * something else entirely: there, they were instructions to a model.
 *
 * Every filter is optional and ANDed. `published` is the live bank, `draft`
 * awaits review, `rejected` was declined — nothing is ever deleted, so this is
 * also where a rejected item can be read back.
 */
/** PRE-285: one screenful. Small enough to scan, large enough to be worth paging. */
const PAGE_SIZE = 25;

export function QuestionBankScreen() {
  const t = useT();
  const label = useEnumLabel();
  const [, setOpenId] = useOpenQuestion();
  const [status, setStatus] = useState<QuestionStatus>('published');
  const [skillId, setSkillId] = useState('');
  const [type, setType] = useState('');
  const [difficulty, setDifficulty] = useState('');
  const [page, setPage] = useState(1);
  const [confirming, setConfirming] = useState(false);

  /**
   * Any filter change resets to page 1. Staying on page 4 of a list that just
   * became one page long shows an empty screen that looks like "no results".
   */
  function narrow(apply: () => void) {
    apply();
    setPage(1);
  }

  /** The filters, in the shape both the list and the discard call want. */
  const filters = {
    skillId: skillId || undefined,
    type: (type || undefined) as QuestionType | undefined,
    difficulty: (difficulty || undefined) as QuestionDifficulty | undefined,
  };

  const q = useDrafts({
    status,
    ...filters,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  });

  const reset = useResetContent();
  const dryRun = useResetContent();

  /**
   * PRE-296: the count is asked for when the dialog opens, not when the button
   * renders. A dry run is a request, and one per filter keystroke would be a
   * request per keystroke to answer a question nobody has asked yet.
   */
  function openConfirm() {
    setConfirming(true);
    dryRun.mutate({
      ...filters,
      status: [status as 'draft' | 'rejected'],
      dryRun: true,
    });
  }
  const items = q.data?.items ?? [];
  const total = q.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Stack gap="md">
      <Card withBorder padding="lg" radius="md">
        <Group justify="space-between" mb="sm" wrap="nowrap">
          <Group gap={6}>
            <Text fw={600}>{t('screen.questions')}</Text>
            <InfoTip label={t('bank.infoTip')} />
          </Group>
          <Button
            size="xs"
            variant="default"
            onClick={() => void q.refetch()}
            loading={q.isFetching}
          >
            {t('common.refresh')}
          </Button>
        </Group>

        <SimpleGrid cols={{ base: 1, xs: 2, md: 4 }} spacing="sm">
          <Select
            label={t('common.status')}
            data={STATUSES.map((s) => ({ value: s, label: label('status', s) }))}
            value={status}
            onChange={(v) => narrow(() => setStatus((v as QuestionStatus) ?? 'published'))}
            allowDeselect={false}
          />
          <SkillPicker
            label={t('common.skill')}
            placeholder={t('content.allSkills')}
            clearable
            value={skillId || null}
            onChange={(v) => narrow(() => setSkillId(v ?? ''))}
          />
          <Select
            label={t('content.type')}
            data={[
              { value: '', label: t('common.all') },
              ...TYPES.map((v) => ({ value: v, label: label('type', v) })),
            ]}
            value={type}
            onChange={(v) => narrow(() => setType(v ?? ''))}
          />
          <Select
            label={t('content.difficulty')}
            data={[
              { value: '', label: t('common.all') },
              ...DIFFICULTIES.map((d) => ({ value: d, label: label('difficulty', d) })),
            ]}
            value={difficulty}
            onChange={(v) => narrow(() => setDifficulty(v ?? ''))}
          />
        </SimpleGrid>

        <Group justify="space-between" mt="sm" align="center">
          {total > 0 ? (
            <Text c="dimmed" size="sm">
              {t('bank.range', {
                from: (page - 1) * PAGE_SIZE + 1,
                to: Math.min(page * PAGE_SIZE, total),
                total,
              })}
            </Text>
          ) : (
            <span />
          )}
          {/*
            PRE-296: discarding is only offered on the two statuses it can
            reach. Showing it on `published` and refusing on click would teach
            the operator that the button is unreliable, when the truth is that
            a published item is not discardable at all — it may have been
            answered, and BR-81 keeps that history where it is.
          */}
          {status !== 'published' && total > 0 && (
            <Button
              size="xs"
              variant="light"
              color="red"
              loading={reset.isPending}
              onClick={openConfirm}
            >
              {t('bank.discardThese', { count: total })}
            </Button>
          )}
        </Group>
      </Card>

      {/*
        The count is re-read from the server rather than taken from `total`,
        which is a page of a list and can be stale by seconds. What the operator
        confirms is what the delete will match.
      */}
      <Modal
        opened={confirming}
        onClose={() => setConfirming(false)}
        title={t('bank.discardTitle')}
        centered
      >
        <Stack gap="sm">
          <Text size="sm">
            {t('bank.discardBody', {
              status: label('status', status),
              unfiltered: skillId || type || difficulty ? '' : t('bank.discardUnfiltered'),
            })}
          </Text>
          {dryRun.data && (
            <Text size="sm">
              {dryRun.data.matched === 1
                ? t('bank.willGo_one')
                : t('bank.willGo', { count: dryRun.data.matched })}
            </Text>
          )}
          {dryRun.isPending && (
            <Text c="dimmed" size="sm">
              {t('bank.counting')}
            </Text>
          )}
          <Text c="dimmed" size="xs">
            {t('bank.publishedSafe')}
          </Text>
          {reset.isError && (
            <Text c="red" size="sm">
              {errorMessage(reset.error)}
            </Text>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirming(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              color="red"
              loading={reset.isPending}
              disabled={dryRun.data?.matched === 0}
              onClick={() => {
                reset.mutate(
                  { ...filters, status: [status as 'draft' | 'rejected'], dryRun: false },
                  {
                    onSuccess: () => {
                      setConfirming(false);
                      setPage(1);
                    },
                  },
                );
              }}
            >
              {t('bank.discard')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {q.isError && (
        <Text c="red" size="sm">
          {errorMessage(q.error)}
        </Text>
      )}
      {q.isLoading && (
        <Text c="dimmed" size="sm">
          {t('common.loading')}
        </Text>
      )}
      {!q.isLoading && items.length === 0 && (
        <Text c="dimmed" size="sm">
          {t('bank.noMatch')}
        </Text>
      )}

      <Stack gap="xs">
        {items.map((d) => (
          <Card key={d.id} withBorder padding="sm" radius="sm">
            <Group justify="space-between" wrap="nowrap" align="flex-start" gap="sm">
              <Anchor
                component="button"
                type="button"
                ta="left"
                size="sm"
                onClick={() => setOpenId(d.id)}
                title={t('question.viewDetail')}
              >
                {d.prompt}
              </Anchor>
              <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
                <Badge variant="light" color="gray">
                  {label('type', d.type)}
                </Badge>
                <Badge variant="light" color="gray">
                  {label('difficulty', d.difficulty)}
                </Badge>
                <Badge variant="light" color={statusColor(d.status)}>
                  {label('status', d.status)}
                </Badge>
                {d.judgeScore != null && (
                  <Badge variant="light" color={d.judgeScore >= 0.6 ? 'green' : 'yellow'}>
                    {t('question.judge', { score: d.judgeScore.toFixed(2) })}
                  </Badge>
                )}
              </Group>
            </Group>
          </Card>
        ))}
      </Stack>

      {pages > 1 && (
        <Group justify="center">
          <Pagination value={page} onChange={setPage} total={pages} siblings={1} withEdges />
        </Group>
      )}

      <QuestionDetailModal />
    </Stack>
  );
}
